(() => {
  if (customElements.get('cosmic-samhain')) return;
  class SamhainReading extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.form = this.querySelector('[data-sh-form]');
      this.question = this.querySelector('[data-sh-question]');
      this.ritual = this.querySelector('[data-sh-ritual]');
      this.ritualNote = this.querySelector('[data-sh-ritual-note]');
      this.button = this.querySelector('[data-sh-submit]');
      this.sticky = this.querySelector('[data-sh-sticky]');
      if (!this.form || !this.button) return;
      this.pending = false;
      this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); }, options);
      this.ritual.addEventListener('change', () => this.render(), options);
      this.querySelectorAll('[data-sh-jump], [data-sh-sticky-jump]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        const order = this.querySelector('[data-sh-order]');
        order.scrollIntoView({ block: 'start', behavior: 'instant' });
        order.querySelector('h2').focus({ preventScroll: true });
      }, options));
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateSticky(); });
      };
      window.addEventListener('scroll', schedule, { ...options, passive: true });
      window.addEventListener('resize', schedule, options);
      document.addEventListener('focusin', schedule, options);
      document.addEventListener('focusout', schedule, options);
      window.visualViewport?.addEventListener('resize', schedule, options);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !this.pending) this.refresh().catch(() => {});
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) { this.pending = false; this.render(); this.refresh().catch(() => {}); }
      }, options);
      this.observer = new ResizeObserver(schedule);
      this.observer.observe(this);
      this.render();
    }
    disconnectedCallback() {
      this.events?.abort(); this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }
    render() {
      const available = this.dataset.state === 'available' && !!this.dataset.variantId;
      const ritualAvailable = this.dataset.ritualState === 'available' && !!this.dataset.ritualId;
      if (!ritualAvailable) this.ritual.checked = false;
      this.ritual.disabled = this.pending || !ritualAvailable;
      this.question.disabled = this.pending;
      this.ritualNote.disabled = this.pending || !this.ritual.checked;
      this.querySelector('[data-sh-ritual-note-wrap]').hidden = !this.ritual.checked;
      const price = this.ritual.checked ? this.dataset.total : this.dataset.price;
      this.querySelectorAll('[data-sh-total]').forEach(el => { el.textContent = available && !this.pending ? price : ''; });
      this.querySelector('[data-sh-base-price]').textContent = this.dataset.price || '';
      this.querySelector('.sh-price-line').hidden = !this.dataset.price;
      this.querySelector('input[name=id]').value = this.dataset.variantId || '';
      const label = this.pending ? 'Opening cart…' : available ? 'Continue' : this.dataset.state === 'sold-out' ? 'Sold out' : 'Coming soon';
      this.button.querySelector('[data-sh-label]').textContent = label;
      this.button.disabled = this.pending || !available;
      this.button.setAttribute('aria-busy', String(this.pending));
      const stickyButton = this.querySelector('[data-sh-sticky-jump]');
      stickyButton.querySelector('[data-sh-label]').textContent = available ? 'Choose my reading' : label;
      stickyButton.disabled = this.pending || !available;
      const state = this.querySelector('[data-sh-state]');
      state.hidden = available;
      state.textContent = this.dataset.state === 'sold-out' ? 'This year’s reading is sold out.' : 'This year’s reading isn’t available to book yet.';
      const ritualState = this.querySelector('[data-sh-ritual-state]');
      ritualState.hidden = ritualAvailable;
      ritualState.textContent = this.dataset.ritualState === 'sold-out' ? 'Ritual sold out' : 'Ritual coming soon';
      this.updateSticky();
    }
    updateSticky() {
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0, bottom = top + (viewport?.height || innerHeight);
      const rect = this.button.getBoundingClientRect();
      const editing = document.activeElement?.matches('textarea,input,select,[contenteditable=true]');
      this.sticky.hidden = !matchMedia('(max-width:760px)').matches || editing || (rect.top >= top + 90 && rect.bottom <= bottom - 100);
    }
    error(message, recover = false) {
      const el = this.querySelector('[data-sh-error]');
      el.textContent = message; el.hidden = !message;
      this.querySelector('[data-sh-recovery]').hidden = !recover;
      if (message) requestAnimationFrame(() => el.scrollIntoView({ block: 'center', behavior: 'instant' }));
    }
    async request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try { return await fetch(url, { ...options, signal: controller.signal }); }
      finally { clearTimeout(timer); }
    }
    async refresh() {
      if (this.refreshing) return this.refreshing;
      this.refreshing = (async () => {
        const url = new URL(location.href); url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await this.request(url, { cache: 'no-store' });
        if (!response.ok) throw new Error('Availability check failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('cosmic-samhain');
        if (!fresh || fresh.dataset.sectionId !== this.dataset.sectionId) throw new Error('Availability check failed');
        for (const key of ['state','ritualState','variantId','ritualId','price','total']) this.dataset[key] = fresh.dataset[key] || '';
        this.querySelector('[data-sh-ritual-price]').textContent = fresh.querySelector('[data-sh-ritual-price]').textContent;
        this.render();
      })().finally(() => { this.refreshing = null; });
      return this.refreshing;
    }
    async saveGuidance(question, withRitual, ritualNote) {
      const response = await this.request(this.dataset.cartUrl + '.js', { cache: 'no-store' });
      if (!response.ok) throw new Error('Your selection is saved, but your answers could not be saved. Please try again.');
      const cart = await response.json();
      const block = ['Samhain reading:\n' + (question || 'Open reading — no questions required.'), withRitual ? 'Samhain ritual:\n' + (ritualNote || 'General ritual — no questions required.') : ''].filter(Boolean).join('\n\n');
      const existing = typeof cart.note === 'string' ? cart.note.trim() : '';
      const alreadySaved = !block || ('\n\n' + existing + '\n\n').includes('\n\n' + block + '\n\n');
      const note = alreadySaved ? existing : [existing, block].filter(Boolean).join('\n\n');
      const attributes = { reading_mode: question ? 'specific' : 'open', samhain_question: question, samhain_ritual_focus: withRitual ? ritualNote : '' };
      const update = await this.request(this.dataset.cartUrl + '/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note, attributes })
      });
      if (!update.ok) throw new Error('Your selection is saved, but your answers could not be saved. Please try again.');
      const updated = await update.json();
      if ((updated.note || '') !== note || Object.entries(attributes).some(([key, value]) => (updated.attributes?.[key] || '') !== value)) {
        throw new Error('Your answers could not be confirmed. Please try again before opening your cart.');
      }
    }
    async submit() {
      if (this.pending || this.dataset.state !== 'available' || !this.dataset.variantId) return;
      const withRitual = this.ritual.checked;
      const previous = JSON.stringify([this.dataset.variantId, this.dataset.price, withRitual && this.dataset.ritualId, withRitual && this.dataset.total]);
      this.pending = true; this.error(''); this.render();
      let navigating = false;
      try {
        const destination = new URL(this.dataset.cartUrl, location.origin);
        if (destination.origin !== location.origin || !destination.pathname.endsWith('/cart')) throw new Error('Your cart is unavailable. Please try again.');
        await this.refresh();
        if (this.dataset.state !== 'available') throw new Error('This reading is no longer available.');
        if (withRitual && this.dataset.ritualState !== 'available') throw new Error('The ritual is no longer available and has been removed. Please review your reading before continuing.');
        const current = JSON.stringify([this.dataset.variantId, this.dataset.price, withRitual && this.dataset.ritualId, withRitual && this.dataset.total]);
        if (previous !== current) throw new Error('The offer has updated. Please review the price and try again.');
        const readingId = Number(this.dataset.variantId), ritualId = Number(this.dataset.ritualId);
        if (!Number.isSafeInteger(readingId) || readingId <= 0 || (withRitual && (!Number.isSafeInteger(ritualId) || ritualId <= 0))) throw new Error('This offer is not available yet.');
        const question = this.question.value.trim(), ritualNote = this.ritualNote.value.trim();
        const signature = JSON.stringify([readingId, question, withRitual ? [ritualId, ritualNote] : null]);
        const response = await this.request(this.dataset.cartUrl + '.js', { cache: 'no-store' });
        if (!response.ok) throw new Error('Your cart could not be opened. Your answers are still here. Please try again.');
        const cart = await response.json();
        const saved = this.attempt ? cart.items.filter(item => item.properties?._samhain_attempt === this.attempt.id) : [];
        if (saved.length) {
          const ids = withRitual ? [readingId, ritualId] : [readingId];
          if (this.attempt.signature !== signature || saved.length !== ids.length || !ids.every(id => saved.some(item => Number(item.variant_id) === id && item.quantity === 1))) {
            this.error('Your earlier selection is already saved. Please review it in your cart before continuing.', true); return;
          }
        } else {
          this.attempt = this.attempt?.signature === signature ? this.attempt : { signature, id: crypto.randomUUID() };
          const properties = { 'What’s been on your mind?': question || 'Open reading', _samhain_attempt: this.attempt.id };
          const items = [{ id: readingId, quantity: 1, properties }];
          if (withRitual) items.push({ id: ritualId, quantity: 1, properties: { 'Ritual focus': ritualNote || 'General ritual', _samhain_attempt: this.attempt.id } });
          const added = await this.request(this.dataset.addUrl + '.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!added.ok) {
            const body = await added.json().catch(() => ({}));
            if (added.status === 422) await this.refresh().catch(() => {});
            throw new Error(typeof body.description === 'string' ? body.description : 'Your cart could not be opened. Please try again.');
          }
        }
        await this.saveGuidance(question, withRitual, ritualNote);
        navigating = true; location.assign(destination.href);
      } catch (error) {
        this.error(error.name === 'AbortError' || error instanceof TypeError || error.message === 'Availability check failed' ? 'Your cart could not be opened. Your answers are still here. Please try again.' : error.message);
      } finally { if (!navigating) { this.pending = false; this.render(); } }
    }
  }
  customElements.define('cosmic-samhain', SamhainReading);
})();
